import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import VariantEditor from './VariantEditor';
import GoalEditor from './GoalEditor';
import './ExperimentCreator.css';

const STEPS = [
  { label: 'Basic Info', key: 'basic' },
  { label: 'Variants', key: 'variants' },
  { label: 'Goals', key: 'goals' },
  { label: 'Targeting', key: 'targeting' },
  { label: 'Review', key: 'review' },
];

const DEFAULT_VARIANTS = [
  { key: 'control', name: 'Control', weight: 50, is_control: true, config: '{}' },
  { key: 'variant_b', name: 'Variant B', weight: 50, is_control: false, config: '{}' },
];

const DEFAULT_GOALS = [
  { event_name: 'purchase', goal_type: 'conversion', is_primary: true },
];

function ExperimentCreator() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    key: '',
    name: '',
    description: '',
    type: 'ab_test',
    site: '',
  });

  const [variants, setVariants] = useState(DEFAULT_VARIANTS);
  const [goals, setGoals] = useState(DEFAULT_GOALS);

  const [targeting, setTargeting] = useState({
    traffic_percentage: 100,
    targeting_rules: '{}',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    if (isEdit) {
      fetchExperiment();
    }
  }, [id]);

  const fetchExperiment = async () => {
    try {
      setFetchLoading(true);
      const response = await axios.get(`/api/experiments/${id}`);
      const exp = response.data;
      setForm({
        key: exp.key || '',
        name: exp.name || '',
        description: exp.description || '',
        type: exp.type || 'ab_test',
        site: exp.site || '',
      });
      if (exp.variants && exp.variants.length > 0) {
        setVariants(exp.variants.map((v) => ({
          key: v.key,
          name: v.name || '',
          weight: v.weight,
          is_control: v.is_control,
          config: typeof v.config === 'string' ? v.config : JSON.stringify(v.config || {}, null, 2),
        })));
      }
      if (exp.goals && exp.goals.length > 0) {
        setGoals(exp.goals.map((g) => ({
          event_name: g.event_name,
          goal_type: g.goal_type,
          is_primary: g.is_primary,
        })));
      }
      setTargeting({
        traffic_percentage: exp.traffic_percentage ?? 100,
        targeting_rules: typeof exp.targeting_rules === 'string'
          ? exp.targeting_rules
          : JSON.stringify(exp.targeting_rules || {}, null, 2),
        start_date: exp.start_date ? exp.start_date.split('T')[0] : '',
        end_date: exp.end_date ? exp.end_date.split('T')[0] : '',
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch experiment:', err);
      setError('Failed to load experiment');
    } finally {
      setFetchLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateTargeting = (field, value) => {
    setTargeting((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      let targetingRules = {};
      try {
        targetingRules = JSON.parse(targeting.targeting_rules || '{}');
      } catch {
        // keep empty
      }

      const variantConfigs = variants.map((v) => {
        let config = {};
        try {
          config = JSON.parse(v.config || '{}');
        } catch {
          // keep empty
        }
        return { ...v, config };
      });

      const payload = {
        ...form,
        variants: variantConfigs,
        goals,
        traffic_percentage: targeting.traffic_percentage,
        targeting_rules: targetingRules,
        start_date: targeting.start_date || null,
        end_date: targeting.end_date || null,
      };

      if (isEdit) {
        await axios.put(`/api/experiments/${id}`, payload);
        navigate(`/experiments/${id}`);
      } else {
        const response = await axios.post('/api/experiments', payload);
        const newId = response.data.id || response.data.experiment?.id;
        navigate(newId ? `/experiments/${newId}` : '/experiments');
      }
    } catch (err) {
      console.error('Failed to save experiment:', err);
      setError(err.response?.data?.error || 'Failed to save experiment');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return form.key.trim() && form.name.trim();
    if (step === 1) return variants.length >= 2;
    return true;
  };

  if (fetchLoading) {
    return (
      <div className="experiment-creator">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading experiment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="experiment-creator">
      <div className="exp-page-header">
        <div>
          <h1>{isEdit ? 'Edit Experiment' : 'New Experiment'}</h1>
          <p className="exp-page-subtitle">
            Step {step + 1} of {STEPS.length}: {STEPS[step].label}
          </p>
        </div>
        <button
          className="exp-btn-secondary"
          onClick={() => navigate(isEdit ? `/experiments/${id}` : '/experiments')}
        >
          Cancel
        </button>
      </div>

      <div className="exp-creator-progress">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`exp-progress-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}>
            <div className="exp-progress-dot">
              {i < step ? <Check size={12} /> : i + 1}
            </div>
            <span className="exp-progress-label">{s.label}</span>
          </div>
        ))}
      </div>

      {error && <div className="exp-error">{error}</div>}

      <div className="exp-creator-body">
        {step === 0 && (
          <div className="exp-step-content">
            <div className="exp-form-group">
              <label className="exp-form-label">Experiment Key</label>
              <input
                type="text"
                className="exp-form-input"
                value={form.key}
                onChange={(e) => updateForm('key', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_'))}
                placeholder="e.g. homepage_hero_test"
                disabled={isEdit}
              />
              <span className="exp-form-hint">Unique identifier used in code. Cannot be changed after creation.</span>
            </div>
            <div className="exp-form-group">
              <label className="exp-form-label">Name</label>
              <input
                type="text"
                className="exp-form-input"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Homepage Hero Test"
              />
            </div>
            <div className="exp-form-group">
              <label className="exp-form-label">Description</label>
              <textarea
                className="exp-form-input"
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                placeholder="What is this experiment testing?"
                rows={3}
              />
            </div>
            <div className="exp-form-row">
              <div className="exp-form-group">
                <label className="exp-form-label">Type</label>
                <select
                  className="exp-form-input"
                  value={form.type}
                  onChange={(e) => updateForm('type', e.target.value)}
                >
                  <option value="ab_test">A/B Test</option>
                  <option value="multivariate">Multivariate</option>
                  <option value="feature_flag">Feature Flag</option>
                </select>
              </div>
              <div className="exp-form-group">
                <label className="exp-form-label">Site</label>
                <input
                  type="text"
                  className="exp-form-input"
                  value={form.site}
                  onChange={(e) => updateForm('site', e.target.value)}
                  placeholder="e.g. rewined.com"
                />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="exp-step-content">
            <VariantEditor variants={variants} onChange={setVariants} />
          </div>
        )}

        {step === 2 && (
          <div className="exp-step-content">
            <GoalEditor goals={goals} onChange={setGoals} />
          </div>
        )}

        {step === 3 && (
          <div className="exp-step-content">
            <div className="exp-form-group">
              <label className="exp-form-label">
                Traffic Percentage: {targeting.traffic_percentage}%
              </label>
              <input
                type="range"
                className="exp-traffic-slider"
                min="1"
                max="100"
                value={targeting.traffic_percentage}
                onChange={(e) => updateTargeting('traffic_percentage', parseInt(e.target.value))}
              />
              <div className="exp-traffic-marks">
                <span>1%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
            <div className="exp-form-group">
              <label className="exp-form-label">Targeting Rules (JSON)</label>
              <textarea
                className="exp-form-input"
                value={targeting.targeting_rules}
                onChange={(e) => updateTargeting('targeting_rules', e.target.value)}
                placeholder='{}'
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>
            <div className="exp-form-row">
              <div className="exp-form-group">
                <label className="exp-form-label">Start Date</label>
                <input
                  type="date"
                  className="exp-form-input"
                  value={targeting.start_date}
                  onChange={(e) => updateTargeting('start_date', e.target.value)}
                />
              </div>
              <div className="exp-form-group">
                <label className="exp-form-label">End Date</label>
                <input
                  type="date"
                  className="exp-form-input"
                  value={targeting.end_date}
                  onChange={(e) => updateTargeting('end_date', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="exp-step-content">
            <h3 className="exp-review-heading">Review Experiment</h3>

            <div className="exp-review-section">
              <h4>Basic Info</h4>
              <div className="exp-review-grid">
                <div className="exp-review-item">
                  <span className="exp-review-label">Key</span>
                  <span className="exp-review-value">{form.key}</span>
                </div>
                <div className="exp-review-item">
                  <span className="exp-review-label">Name</span>
                  <span className="exp-review-value">{form.name}</span>
                </div>
                <div className="exp-review-item">
                  <span className="exp-review-label">Type</span>
                  <span className="exp-review-value">{form.type}</span>
                </div>
                <div className="exp-review-item">
                  <span className="exp-review-label">Site</span>
                  <span className="exp-review-value">{form.site || '--'}</span>
                </div>
              </div>
              {form.description && (
                <p className="exp-review-desc">{form.description}</p>
              )}
            </div>

            <div className="exp-review-section">
              <h4>Variants ({variants.length})</h4>
              {variants.map((v, i) => (
                <div key={i} className="exp-review-variant">
                  <span className="exp-review-variant-key">{v.key}</span>
                  <span>{v.name || '--'}</span>
                  <span>{v.weight}%</span>
                  {v.is_control && <span className="variant-control-badge">Control</span>}
                </div>
              ))}
            </div>

            <div className="exp-review-section">
              <h4>Goals ({goals.length})</h4>
              {goals.map((g, i) => (
                <div key={i} className="exp-review-goal">
                  <span className="exp-review-goal-event">{g.event_name}</span>
                  <span>{g.goal_type}</span>
                  {g.is_primary && <span className="goal-primary-badge">Primary</span>}
                </div>
              ))}
            </div>

            <div className="exp-review-section">
              <h4>Targeting</h4>
              <div className="exp-review-grid">
                <div className="exp-review-item">
                  <span className="exp-review-label">Traffic</span>
                  <span className="exp-review-value">{targeting.traffic_percentage}%</span>
                </div>
                <div className="exp-review-item">
                  <span className="exp-review-label">Start Date</span>
                  <span className="exp-review-value">{targeting.start_date || 'Not set'}</span>
                </div>
                <div className="exp-review-item">
                  <span className="exp-review-label">End Date</span>
                  <span className="exp-review-value">{targeting.end_date || 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="exp-creator-footer">
        <button
          className="exp-btn-secondary"
          disabled={step === 0}
          onClick={() => setStep((s) => s - 1)}
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="exp-creator-footer-right">
          {step < STEPS.length - 1 ? (
            <button
              className="exp-btn-primary"
              disabled={!canProceed()}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              className="exp-btn-primary"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? 'Saving...' : isEdit ? 'Update Experiment' : 'Create Experiment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExperimentCreator;
